import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTelegram } from '@/shared/lib/telegram'
import { useTeaCategories } from '@/entities/product/api/use-categories'
import { motion } from 'motion/react'
import { ChevronRight } from 'lucide-react'
import { Skeleton } from '@/shared/ui/skeleton'

/**
 * Виджет: Быстрые категории (чипы)
 * 
 * Горизонтальный ряд категорий для быстрого доступа к каталогу с фильтрами.
 * При клике открывает каталог с примененным фильтром.
 * Категории загружаются с API.
 */
export function HomeCategories() {
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()
  
  // Получаем категории с API
  const { data: categoriesData, isLoading } = useTeaCategories()
  const categories = categoriesData?.data || []
  
  // Сортируем категории по sortOrder
  const sortedCategories = [...categories].sort((a, b) => {
    const orderA = a.sortOrder ?? 999
    const orderB = b.sortOrder ?? 999
    return orderA - orderB
  })
  
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  
  const handleCategoryClick = (categoryId: string) => {
    hapticFeedback.impactOccurred('light')
    setActiveCategory(categoryId)
    // Используем categoryId (UUID) для фильтрации
    navigate({
      to: '/catalog/$section',
      params: { section: 'tea' },
      search: { category: categoryId },
    })
  }
  
  // Skeleton при загрузке
  if (isLoading) {
    return (
      <div className="widget-card">
        <div className="flex items-center justify-between mb-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-5 w-24" />
        </div>
        <div className="flex gap-3 overflow-x-auto lg:flex-wrap pb-2 lg:pb-0 scrollbar-hide">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-24 rounded-[14px] flex-shrink-0" />
          ))}
        </div>
      </div>
    )
  }
  
  // Если категорий нет, не показываем компонент
  if (sortedCategories.length === 0) {
    return null
  }
  
  return (
    <div className="widget-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="section-title text-lg sm:text-xl">Популярные направления</h2>
        <button
          onClick={() => {
            hapticFeedback.impactOccurred('light')
            navigate({ 
              to: '/catalog/$section',
              params: { section: 'tea' }
            })
          }}
          className="text-sm text-primary/80 dark:text-primary-light font-medium hover:text-primary dark:hover:text-primary-light transition-colors flex items-center gap-1 touch-target"
        >
          Смотреть всё
          <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
      {/* На mobile - горизонтальный скролл, на desktop - flex wrap */}
      <div className="flex gap-3 overflow-x-auto lg:flex-wrap pb-2 lg:pb-0 scrollbar-hide">
        {sortedCategories.map((category) => {
          const isActive = activeCategory === category.id
          return (
            <motion.button
              key={category.id}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleCategoryClick(category.id)}
              className={`
                flex-shrink-0 px-4 py-2.5 rounded-[14px]
                transition-all touch-target font-medium text-sm
                shadow-sm dark:shadow-md
                ${
                  isActive
                    ? 'bg-primary/20 dark:bg-primary-dark/20 border-2 border-primary dark:border-primary-dark text-primary dark:text-primary-light'
                    : 'bg-silk/30 backdrop-blur-sm border border-silk/50 text-ink hover:bg-silk/50 hover:border-primary/30 dark:bg-dark-elevated dark:border-dark-border dark:text-white dark:hover:bg-dark-card'
                }
              `}
            >
              {category.name}
            </motion.button>
          )
        })}
      </div>
    </div>
  )
}
